import { useCallback, useMemo } from 'react';
import type {
  AdvancedReportsAgentPerformanceResponse,
  AdvancedReportsConfigResponse,
} from 'src/api/generated';
import {
  type AgentPerformanceCsvLabels,
  buildAgentPerformanceCsvContent,
  downloadCsvFile,
} from '../utils/agent-performance-export';

interface UseAgentPerformanceCsvExportParams {
  response: AdvancedReportsAgentPerformanceResponse | undefined;
  rows?: AdvancedReportsAgentPerformanceResponse['rows'];
  config: AdvancedReportsConfigResponse | undefined;
  labels: AgentPerformanceCsvLabels;
}

export const useAgentPerformanceCsvExport = ({
  response,
  rows,
  config,
  labels,
}: UseAgentPerformanceCsvExportParams) => {
  const exportRows = useMemo(
    () => rows ?? response?.rows ?? [],
    [response?.rows, rows]
  );
  const hasRows = exportRows.length > 0;

  const filename = useMemo(() => {
    if (!response) {
      return null;
    }

    const safeFrom = response.resolvedDateRange.from.slice(0, 10);
    const safeTo = response.resolvedDateRange.to.slice(0, 10);
    return `agent-performance-${safeFrom}-to-${safeTo}.csv`;
  }, [response]);

  const handleDownload = useCallback(() => {
    if (!exportRows.length || !filename) {
      return;
    }

    const csvContent = buildAgentPerformanceCsvContent(
      exportRows,
      config?.showContactability !== false,
      labels
    );
    downloadCsvFile(filename, csvContent);
  }, [config?.showContactability, exportRows, filename, labels]);

  return {
    hasRows,
    filename,
    handleDownload,
  };
};
